"use client";
import {
  Function,
  Person,
  SquadPositionPlayer,
  Team,
} from "@/sources/kicker-amateur/types";
import React, { useCallback, useEffect, useState } from "react";
import Button from "@/app/pwa/components/Button";
import Share from "@/assets/icon/share.svg";
import Rotate from "@/assets/icon/rotate.svg";
import IconCopy from "@/assets/icon/copy.svg";
import IconCheckmark from "@/assets/icon/checkmark.svg";
import Mail from "@/assets/icon/mail.svg";
import Whatsapp from "@/assets/icon/whatsapp.svg";
import Telegram from "@/assets/icon/telegram.svg";
import {
     EmailShareButton,
  TelegramShareButton,
  WhatsappShareButton,
} from "react-share";
import { ErrorText } from "@/app/pwa/components/form/ErrorText";
import Modal, { ModalProps, ModalTitle } from "@/app/pwa/components/Modal";
import { QRCode } from "@/app/pwa/components/QRCode";
import { useShareHook } from "@/hooks/useShareHook";
import { UserInfo } from "@/sources/kicker-ovsyndication";
import FunctionImage from "../FunctionImage";
import { getPersonFullName } from "@/helpers/personHelpers";
import { getSsoProfileFirstName } from "@/helpers/profileHelpers";
import { apiInvitationCreatePath, pwaInvitationConfirmUrl } from "@/utils/urlHelper";
import { usePwa } from "../../lib/pwa-context";


//TODO move to own component
interface ShareInvitationProps {
  url: string;
  team: Team;
  player?: SquadPositionPlayer | Person;
  user?: UserInfo;
}

function ShareInvitation({ url, team, player, user }: ShareInvitationProps) {
  const clubName = team.ParentOrganization?.OfficalName;
  const playerName = getPersonFullName(player as Person);
  const userFirstName = user ? getSsoProfileFirstName(user) : undefined;

  const [isCopied, setIsCopied] = useState(false);

  const subject = `Einladung zum kicker Vereinsheim von ${clubName}`;
  const body = `Hallo ${playerName}, \n\nhier ist deine Einladung zu unserem kicker Vereinsheim. \n \nViele Grüße ${userFirstName}\n\n`;
  const shareData = {
    title: subject,
    text: body,
    url: url,
  };
  const { canShare, toggleShare } = useShareHook(shareData);

  async function copy() {
    if (!isCopied) {
      try {
        await navigator.clipboard.writeText(url);
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      } catch (e) {
        //TODO: handle error
      }
    }
  }
  return (
    <div className="flex items-center justify-center flex-col pb-6">
      <h2 className="text-lg font-bold">Einladungslink teilen</h2>
      <div className="pt-1 pb-2 w-full">
        {!isCopied ? (
          <div className="flex justify-center items-center">
            <input
              className="appearance-none outline-none flex-1 bg-transparent"
              value={url}
              readOnly
            />
            <button onClick={() => copy()} className="button button-secondary">
              <IconCopy className="text-secondary" />
            </button>
          </div>
        ) : (
          <div className="flex justify-center items-center gap-2">
            <span onClick={() => setIsCopied(false)}>Link wurde kopiert!</span>
            <button
              onClick={() => setIsCopied(false)}
              className="button button-secondary -mx-4"
            >
              <IconCheckmark className="text-secondary" />
            </button>
          </div>
        )}
      </div>
      <div className="flex flex-col relative">

        <div className="flex items-center justify-center gap-2">
          <EmailShareButton subject={subject} body={body} separator=" " url={url}>
            <div className="button button--primary p-0 h-12 w-12 justify-center" style={{background: "#7f7f7f"}}>
              <Mail className="text-lg" />
            </div>
          </EmailShareButton>
          <WhatsappShareButton url={url} title={subject}>
            <div className="button button--primary p-0 h-12 w-12 justify-center" style={{background: "#25D366"}}>
              <Whatsapp className="text-lg" />
            </div>
          </WhatsappShareButton>
          <TelegramShareButton url={url} title={subject}>
            <div className="button button--primary p-0 h-12 w-12 justify-center" style={{background: "#37AEE2"}}>
              <Telegram className="text-lg" />
            </div>
          </TelegramShareButton>
          {canShare && <Button color="secondary" className="p-0 h-12 w-12 justify-center" onClick={toggleShare}>
            <Share className="text-lg" />
          </Button>}
        </div>

      </div>
    </div>
  );
}

interface ErrorSectionProps {
  error: string;
  onRetry: () => void;
  loading: boolean;
}

function ErrorSection({ error, onRetry, loading }: ErrorSectionProps) {
  return (
    <div className="text-center">
      <p className="font-bold">Einladung konnte nicht geladen werden</p>
      <ErrorText className={`my-2 text-sm`}>{error}</ErrorText>
      <Button loading={loading} color="primary" onClick={onRetry}>
        Versuche es nochmal <Rotate className="ml-2" />
      </Button>
    </div>
  );
}

type InvitationOptionsModalProps = {
  player?: SquadPositionPlayer | Person;
  team: Team;
  user: UserInfo;
  clubId: number
} & ModalProps;

export function InvitationOptionsModal({
  player,
  team,
  user,
  clubId,
  children,
  ...rest
}: InvitationOptionsModalProps) {
  const [backendError, setBackendError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState<string>();

  const sendInvitation = useCallback(async () => {
    setBackendError(null);
    setLoading(true);
    const res = await fetch(apiInvitationCreatePath(), {
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify({
        personId: `${player?.Id}`,
        contextId: player?.TeamId ? `${player?.TeamId}` : `${team.Id}`,
        contextType: "Team",
      }),
    });
    if (res.ok) {
      const message = await res.json();
      const origin = window.location.origin;
      setUrl(pwaInvitationConfirmUrl(origin, message.token));
    } else {
      const message = await res.json();
      if (message.error) {
        setBackendError(message.error);
      }
    }
    setLoading(false);
  }, [player?.Id, player?.TeamId, team.Id]);

  useEffect(() => {
    if (player?.Id) {
      sendInvitation();
    }
  }, [player?.Id, sendInvitation]);

  return (
    <Modal {...rest} title={`${player?.FirstName} EINLADEN`} loading={!url}>
      {url && (
        <div className="flex flex-col space-y relative max-w-lg">
          <ModalTitle className="px-6 w-full">{player?.FirstName} einladen</ModalTitle>
          <div className="mt-4">
            <div className="flex gap-4 justify-center border-b border-gray-150 pb-8">
              <div className="w-16 shrink-0">
                <FunctionImage className="w-full" player={player} functionId={(player?.Function as Function)?.Id} size="sm" />
              </div>
              <div>
                <div className="font-semibold">
                  Die eingeladene Person kann z. B.
                </div>
                <ul className="list-disc pl-4 ml-4">
                  <li>Trainings- bzw. Spielteilnahmen bestätigen</li>
                  <li>an Abstimmungen teilnehmen</li>
                </ul>
              </div>
            </div>

            <div className="flex items-center justify-center flex-col py-8 border-b border-gray-150">
              <div className="text-lg font-semibold pb-4">
                Einladung mit QR-Code
              </div>
              <QRCode url={url} />
              <div className="text-gray-200 mt-4 text-center">
                {player?.FirstName} kann diesen QR-Code scannen um, die Einladung
                zu öffnen.
              </div>
            </div>
          </div>

          <div className="pt-8">
            <ShareInvitation
              url={url}
              team={team}
              player={player}
              user={user}
            />
            {backendError && (
              <ErrorSection
                error={backendError}
                onRetry={sendInvitation}
                loading={loading}
              />
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
